import { memo } from 'react';
import Link from 'next/link';
import Url from 'lib/siteurls';

import { PosterEl, ParagraphEl } from 'components/site/site_sections';
import RichTextRenderer from 'components/misc/rich-text-renderer';
import { RootState } from 'entities/rootState';
import styled from 'styled-components';
import {
  mqXXLargeMin,
  mqSmallMin,
  mqLargeMin,
} from 'styled/tools/media-queries';
import { WithFontHead, WithSiteLayout } from 'styles/commonCss';
import Image from 'next/image';
import { getContentField } from 'lib/contentful/get-content-field';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import { mqSXLargeMin } from 'styled/tools/media-queries';
import { buttonEventHandler } from 'lib/utils/button-event-handler';

type Props = {
  data: any;
} & RootState;

const StyledSection = styled.section`
  display: block;
  background-color: var(--fill-secondary);
  padding: var(--spacing-48) 0;

  @media ${mqLargeMin} {
    padding: 100px 0;
  }

  * {
    color: var(--text-primary);
  }

  > .container {
    ${WithSiteLayout};
    height: auto;
    position: relative;
    display: block;
  }

  .body img {
    width: 100%;
  }

  .foot {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    @media ${mqLargeMin} {
      margin: var(--spacing-48) 0 0;
    }
  }

  .text {
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    margin: 0 0 var(--spacing-24);

    @media ${mqLargeMin} {
      width: 380px;
      margin: 0;
    }

    @media ${mqXXLargeMin} {
      width: 420px;
    }
  }

  .text h2 {
    ${WithFontHead};
    letter-spacing: var(--flag-letter-spacing-1);
    @media ${mqSmallMin} {
      letter-spacing: var(--flag-letter-spacing-2);
    }
    padding: 0 0 var(--spacing-24) 0;
    font-size: 36px;
    text-align: left;
    display: block;

    @media ${mqLargeMin} {
      font-size: 50px;
      padding: 0;
    }
  }

  .text .blurb {
    display: block;
    margin-top: var(--spacing-24);
    font-size: 14px;
  }

  .text .blurb span {
    font-weight: 400;
  }

  .text a {
    display: flex;
    align-items: center;
    text-decoration: none;
    cursor: pointer;
    margin: var(--spacing-24) 0 0;
    width: 100px;

    @media ${mqLargeMin} {
      margin: 30px 0 0;
    }

    i {
      display: flex;
      margin: 0 10px 0 0;
    }

    p {
      display: flex;

      b {
        font-size: 14px;

        &:first-of-type {
          position: relative;
          display: flex;
          align-items: center;

          &::after {
            content: '';
            display: block;
            position: relative;
            width: 9px;
            height: 9px;
            border-radius: 50%;
            margin: 0 20px;
            background-color: var(--fill-brand);
            transition: all 0.25s ease;
          }
        }
      }
    }

    &:hover,
    &:focus {
      p b::after,
      .text a:hover b::after {
        background-color: #fff;
      }
    }
  }

  .text .blurb a span {
    font-weight: 500;
    font-size: 12px;
  }

  .text .button {
    display: none;
    text-transform: inherit;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 60px;

    @media ${mqLargeMin} {
      display: flex;
    }

    span {
      font-size: 14px;
    }

    i {
      transform: scale(0.6);
      padding: 0 var(--spacing-24);
    }
  }

  .text img {
    display: block;
    margin: var(--spacing-24) auto 0;

    @media ${mqLargeMin} {
      display: none;
    }
  }

  .media {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    position: relative;
    width: 100%;

    @media ${mqLargeMin} {
      width: auto;
    }
  }

  .media .container {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: flex-start;
    width: 100%;
  }

  .media .poster {
    display: block;
    text-align: center;
    width: 100%;
    max-width: 100%;

    @media ${mqSmallMin} {
      width: 50%;
    }

    @media ${mqLargeMin} {
      width: 250px;
    }

    @media ${mqXXLargeMin} {
      width: 300px;
    }
  }

  .media .poster:last-child {
    margin: var(--spacing-24) 0 0;

    @media ${mqSmallMin} {
      margin: 0;
    }

    @media ${mqLargeMin} {
      margin: 0 0 0 var(--spacing-24);
    }

    @media ${mqXXLargeMin} {
      margin: 0 0 0 62px;
    }
  }

  .media .poster .thumb {
    display: flex;
    justify-content: center;
    background-blend-mode: multiply;
    background-repeat: no-repeat;
    background-position: center;
    background-color: var(--fill-secondary);
    height: 270px;
    width: 100%;

    @media ${mqLargeMin} {
      height: 325px;
      border-bottom: 1px solid var(--outline-primary);
    }
  }

  .media .poster:first-child .thumb {
    background-size: auto 105%;
    background-position: center;

    @media ${mqLargeMin} {
      background-size: auto 112%;
      background-position: -65px -35px;
    }

    @media ${mqXXLargeMin} {
      background-size: auto 112%;
      background-position: -50px -35px;
    }
  }

  .media .poster:last-child .thumb {
    background-size: auto 100%;
    background-position: center;

    @media ${mqLargeMin} {
      background-size: auto 80%;
      background-position: center 50px;
    }
  }

  .media .poster .caption {
    display: block;
    padding: var(--spacing-8) 0 0;
    font-size: 0;

    @media ${mqLargeMin} {
      padding: 30px 0 0;
    }
  }

  .media .poster .caption h3 {
    margin-bottom: var(--spacing-8);
    font-weight: 500;
  }

  .media .poster .caption * {
    font-size: 16px;
    text-align: center;
    display: block;

    @media ${mqLargeMin} {
      text-align: left;
    }
  }

  // VIDEO
  .video {
    position: relative;
    display: block;
  }

  [data-vimeo-initialized] {
    display: block;
    width: 100%;

    > div {
      @media ${mqLargeMin} {
        padding: 56% 0 0 !important;
      }
    }
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: transparent;
  }
`;

const StyledImage = styled(Image)`
  @media (max-width: 64em) {
    max-width: 100%;
    max-height: 100%;
    width: 80% !important;
    object-fit: contain;
  }

  @media ${mqLargeMin} {
    padding-top: 58px;
    max-height: 100%;
  }
`;

const SectionSustainability = memo(({ page, cms, data }: Props) => {
  const videoData =
    data?.fields?.reference?.filter((item) => item.fields.title === 'video') ||
    null;
  const textData =
    data?.fields?.reference?.filter((item) => item.fields.title === 'text') ||
    null;

  const richTextSubtitle = getContentField({
    data,
    title: 'rich-text-subtitle',
  });

  const titleData = getContentField({ data, title: 'title' });

  const buttonData = getContentField({ data, title: 'button-text' });

  const month1Data = getContentField({ data, title: 'welcome-kit' });

  const month2Data = getContentField({ data, title: 'sustainable-refills' });

  const month1Image = getContentField({ data, title: 'welcome-kit-image' });
  const month1ImageUrl = month1Image?.image[0].secure_url.replace(
    '/upload/',
    '/upload/e_replace_color:f6f7ef:1000:white/',
  );

  const month2Image = getContentField({
    data,
    title: 'sustainable-refills-image',
  });
  const month2ImageUrl = month2Image?.image[0].secure_url.replace(
    '/upload/',
    '/upload/e_replace_color:f6f7ef:1000:white/',
  );

  const size = useClientSideMediaQuery(mqSXLargeMin) ? 'lg' : 'sm';

  const siteBuyContent =
    cms?.siteBuy?.find((v) => page === (v?.fields?.title || null)) ||
    cms?.siteBuy?.find((v) => (v?.fields?.title || null) === 'daily-synbiotic');
  const btn = siteBuyContent?.fields?.button || null;

  const FooterText = memo(() => (
    <div className='text'>
      <div className='container'>
        {/* TSREVIEW: */}
        {/* @ts-ignore */}
        <RichTextRenderer content={titleData} />

        {size === 'sm' && videoData && (
          <PosterEl
            data={videoData[0]}
            withAnchor
            // @ts-ignore
            onVisible
            checkOnlyVisible
          />
        )}

        {textData && (
          // @ts-ignore
          <ParagraphEl data={textData[0]} />
        )}

        {richTextSubtitle && <RichTextRenderer content={richTextSubtitle} />}

        {btn && (
          <Link href={Url.buy} prefetch={false} legacyBehavior>
            <button
              onClick={() =>
                buttonEventHandler('DS start now', 'PDP sustainability')
              }
              className='button button-outline-accent'
              type='button'
            >
              {/* TSREVIEW: */}
              {/* @ts-ignore */}
              <RichTextRenderer content={buttonData} />
            </button>
          </Link>
        )}
      </div>
    </div>
  ));
  const footerMedia = (
    <div className='media'>
      <div className='container'>
        <div className='poster'>
          {month1Image?.image && (
            <div className='thumb'>
              <div>
                <StyledImage
                  src={month1ImageUrl}
                  alt={month1Image.altText || 'DS-01 welcome kit'}
                  width={713}
                  height={755}
                  layout='responsive'
                />
              </div>
            </div>
          )}
          {month1Data && (
            <div className='caption'>
              {/* TSREVIEW: */}
              {/* @ts-ignore */}
              <RichTextRenderer content={month1Data} />
            </div>
          )}
        </div>

        <div className='poster'>
          {month2Image?.image && (
            <div className='thumb'>
              <div>
                <StyledImage
                  src={month2ImageUrl}
                  alt={month2Image.altText || 'DS-01 sustainable refill packet'}
                  width={498}
                  height={755}
                  layout='responsive'
                />
              </div>
            </div>
          )}
          {month2Data && (
            <div className='caption'>
              {/* TSREVIEW: */}
              {/* @ts-ignore */}
              <RichTextRenderer content={month2Data} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
  return (
    <StyledSection>
      <div className='container'>
        {size === 'lg' && videoData && (
          <PosterEl
            data={videoData[0]}
            withAnchor
            // @ts-ignore
            onVisible
            checkOnlyVisible
          />
        )}
        <div className='foot'>
          <FooterText />
          {footerMedia}
        </div>
      </div>
    </StyledSection>
  );
});

export default SectionSustainability;
